import { Link, useRouter } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Requirement {
  label: string;
  test: (v: string) => boolean;
}

const REQUIREMENTS: Requirement[] = [
  { label: "At least 12 characters", test: (v) => v.length >= 12 },
  { label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
  { label: "One number", test: (v) => /[0-9]/.test(v) },
  { label: "One symbol", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export function ResetPasswordPage() {
  const router = useRouter();
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      router.navigate({ to: "/forgot-password" });
    } else {
      setToken(t);
    }
  }, [router]);

  const allRequirementsMet = REQUIREMENTS.every((r) => r.test(password));
  const passwordsMatch = password === confirm;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allRequirementsMet) {
      setError("Password does not meet the requirements.");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data: { error?: string; message?: string; ssoUser?: boolean } = await res.json();

      if (!res.ok) {
        if (data.ssoUser) {
          setError(
            "Your account uses company SSO. Please reset your password through your identity provider.",
          );
          return;
        }
        setError(data.message ?? "Failed to reset password. The link may have expired.");
        return;
      }
      setSuccess(true);
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (!token) return null;

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-950">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/login-bg.jpg')" }}
      />
      <div className="absolute inset-0 bg-slate-950/75" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />

      <div className="relative z-10 w-full max-w-md px-4">
        <div className="mb-8 flex flex-col items-center">
          <img
            src="/logo-white.png"
            alt="Prominent Builders"
            className="h-auto w-72 drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]"
          />
        </div>

        <Card className="border-slate-700/50 bg-slate-800/80 shadow-2xl backdrop-blur">
          <CardContent className="pt-6">
            {success ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <div className="rounded-full bg-green-500/10 p-3 ring-1 ring-green-500/20">
                  <CheckCircle2 className="size-8 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Password updated</h3>
                <p className="text-sm text-slate-400">
                  Your password has been changed. All existing sessions have been signed out.
                </p>
                <Link
                  to="/login"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#415A77] px-4 py-2 text-sm font-medium text-white hover:bg-[#4E6B8A]"
                >
                  Sign in with new password
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <h2 className="text-lg font-semibold text-white">Set new password</h2>
                  <p className="text-xs text-slate-400">
                    Your new password will apply to all sign-in methods.
                  </p>
                </div>

                {error && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-400 ring-1 ring-red-500/20">
                    <AlertCircle className="mt-0.5 size-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">
                    New password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="At least 12 characters"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="border-slate-600 bg-slate-700/50 pr-10 text-white placeholder:text-slate-500 focus-visible:ring-[#415A77]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                </div>

                {password.length > 0 && (
                  <ul className="space-y-1">
                    {REQUIREMENTS.map((r) => (
                      <li
                        key={r.label}
                        className={`flex items-center gap-2 text-xs ${r.test(password) ? "text-green-400" : "text-slate-500"}`}
                      >
                        <span className="size-1.5 rounded-full bg-current" />
                        {r.label}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-slate-300">
                    Confirm password
                  </Label>
                  <Input
                    id="confirm"
                    type="password"
                    placeholder="Repeat your password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className={`border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-500 focus-visible:ring-[#415A77] ${
                      confirm.length > 0 && !passwordsMatch ? "border-red-500/50" : ""
                    }`}
                  />
                  {confirm.length > 0 && !passwordsMatch && (
                    <p className="text-xs text-red-400">Passwords do not match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading || !allRequirementsMet || !passwordsMatch}
                  className="w-full bg-[#415A77] text-white hover:bg-[#4E6B8A] disabled:opacity-50"
                >
                  {loading ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                  Update Password
                </Button>

                <Link
                  to="/login"
                  className="flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-300"
                >
                  <ArrowLeft className="size-3.5" />
                  Back to sign in
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
